import { Card, CardContent, CardHeader, CardTitle, FormBuilderFields } from '@folio/components';
import { useQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  type GetBrokerageFirmOutputFirmTypeEnumKey,
  type PublicQuoteConfig,
} from 'korint-sdk';
import type { Fields } from 'korint-sdk/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isComponentEditableBy } from './manual-components';

export {
  type ManualComponentFormMap,
  fromSavedManualComponents,
  isComponentEditableBy,
  toManualComponentPayload,
  useManualComponentsConfig,
  useManualComponentValueTypes,
} from './manual-components';

export type ManualComponentsGroup = {
  id: string;
  componentIds: string[];
  /** Number of fields per row. When set, fields get colSpan calculated from 12/perRow. */
  perRow?: number;
  /** Per-component colSpan overrides. Takes priority over perRow. */
  colSpanOverrides?: Record<string, number>;
  /** Per-component placeholder text. */
  placeholders?: Record<string, string>;
};

type ManualComponentsCardProps = {
  productId: string;
  activeTierId: string | undefined;
  firmType: GetBrokerageFirmOutputFirmTypeEnumKey | undefined;
  /** When provided, renders one card per group; ungrouped components fall into a trailing default card. */
  groups?: ManualComponentsGroup[];
};

export function ManualComponentsCard({
  productId,
  activeTierId,
  firmType,
  groups,
}: ManualComponentsCardProps) {
  const { data: config } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId),
  });
  const { t } = useTranslation();

  const publicQuoteConfig = config?.publicQuoteConfig as PublicQuoteConfig | undefined;
  const tier = publicQuoteConfig?.tiers.find(t => t.id === activeTierId);
  const editableComponents = (tier?.manualComponents ?? []).filter(c =>
    isComponentEditableBy(c, firmType),
  );

  const toFields = (
    componentIds: string[],
    perRow?: number,
    colSpanOverrides?: Record<string, number>,
    placeholders?: Record<string, string>,
  ): Fields => {
    const byId = new Map(editableComponents.map(c => [c.id, c]));
    const defaultColSpan = perRow ? ((12 / perRow) as 1 | 2 | 3 | 4 | 6 | 12) : undefined;
    return componentIds
      .map(id => byId.get(id))
      .filter((c): c is NonNullable<typeof c> => c !== undefined)
      .map(c => {
        const colSpan = (colSpanOverrides?.[c.id] ?? defaultColSpan) as
          | 1
          | 2
          | 3
          | 4
          | 6
          | 12
          | undefined;
        return {
          name: `manualComponents.${c.id}`,
          type: 'number' as const,
          required: c.required,
          min: 0,
          max: c.valueType === 'PERCENTAGE' ? 100 : undefined,
          ...(colSpan && { colSpan }),
          ...(placeholders?.[c.id] && { DEPRECATED_placeholder: placeholders[c.id] }),
        };
      });
  };

  const sections = useMemo(() => {
    if (!groups || groups.length === 0) {
      return [
        {
          title: t('manualComponentsCard.title'),
          fields: toFields(editableComponents.map(c => c.id)),
        },
      ];
    }

    const groupedIds = new Set(groups.flatMap(g => g.componentIds));
    const ungrouped = editableComponents.filter(c => !groupedIds.has(c.id));
    const result = groups.map(g => ({
      title: t(`manualSection.${g.id}`),
      fields: toFields(g.componentIds, g.perRow, g.colSpanOverrides, g.placeholders),
    }));
    if (ungrouped.length > 0) {
      result.push({
        title: t('manualComponentsCard.title'),
        fields: toFields(ungrouped.map(c => c.id)),
      });
    }
    return result;
    // biome-ignore lint/correctness/useExhaustiveDependencies: toFields uses editableComponents, productId, t — captured via closure each render
  }, [groups, editableComponents, productId, t]);

  const sectionsWithFields = sections.filter(s => s.fields.length > 0);
  if (sectionsWithFields.length === 0) return null;

  return (
    <>
      {sectionsWithFields.map(section => (
        <Card key={section.title}>
          <CardHeader>
            <CardTitle>{section.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <FormBuilderFields fields={section.fields} />
          </CardContent>
        </Card>
      ))}
    </>
  );
}
